import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormGroup, FormArray, AbstractControl } from '@angular/forms';
import { BhtpStateService, State, SelectItem, AddressValidState, BhtpJurisdictionsService} from '@bhtp-client-api/eligibility';
import { TravelerDetailComponent, TravelerDetailViewModel, TravelerDetailViewConfig, AddressViewConfig, Mapper, AddressViewModel, ModalService } from '@bhtp-client-web/component';
import { Observable, Subject } from 'rxjs';
import { EditSectionComponent } from './edit-section.component';
import { TravelersData } from './view-models/travelers-data.model';
import { Traveler } from './../shared';
import { AddressResult } from '../shared/models/address-result.model';
import { IApiAddress } from '@bhtp-client-api/base';

@Component({
    selector: 'travelers',
    template: require('./travelers.component.html')
})
export class TravelersComponent extends EditSectionComponent<TravelersData> implements OnChanges {
    @Input() public maxTravelers: number = 25;
    @Input() public policyHolderConfig: TravelerDetailViewConfig;
    @Input() public additionalTravelerConfig: TravelerDetailViewConfig;
    @Input() public newTravelerConfig: TravelerDetailViewConfig;
    @Input() public addressConfig: AddressViewConfig;
    @Input() public canAdd: boolean;
    @Input() public canRemove: boolean;
    @Input() public states: State[] = [];

    @Input()
    public set travelersData(value: TravelersData) {
        if (!this._travelersData && value && value.primaryTraveler && value.primaryTraveler.address) {
            this.originalResidenceStateIso2 = value.primaryTraveler.address.stateOrProvince;
        }

        this._travelersData = value;
    }
    public get travelersData(): TravelersData {
        return this._travelersData;
    }

    public get policyHolderPostalError(): string {
        return (this.policyHolderAddressResult && this.policyHolderAddressResult.errorMessage) || '';
    }

    private _travelersData: TravelersData;
    private formInitialized: boolean = false;
    private addressForm: FormGroup;
    private policyHolderForm: FormGroup;
    private additionalTravelerForms: FormArray;
    private newTravelerForms: FormArray;
    private newTravelersData: Array<Traveler> = [];

    private policyHolderAddressResult: AddressResult;
    private originalResidenceStateIso2: string;
    private addressChanges: Subject<AddressViewModel> = new Subject<AddressViewModel>();

    constructor(private bhtpStateService: BhtpStateService, private modalService: ModalService, private jurisdictionsService: BhtpJurisdictionsService) {
        super();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (this.travelersData && !this.formInitialized) {
            this.buildForm();
        }
    }

    public buildForm(): void {
        this.policyHolderAddressResult = new AddressResult();

        this.policyHolderForm = new FormGroup({});
        this.addressForm = new FormGroup({});
        this.newTravelerForms = new FormArray([]);
        this.additionalTravelerForms = new FormArray([]);

        if (this.travelersData.additionalTravelers) {
            this.travelersData.additionalTravelers.forEach(t => {
                this.additionalTravelerForms.push(new FormGroup({}));
            });
        }

        this.addressForm
            .valueChanges
            .subscribe((formValues: AddressViewModel) => {
                if (this.policyHolderAddressResult && this.policyHolderAddressResult.result && formValues &&
                        this.policyHolderAddressResult.result.postalCode === formValues.postalCode) {
                    // no need to do anything. we have a valid result being set into the form.
                    return;
                }

                // the value being set is different from what we previously verified (or didn't verify, yet).
                this.updateAddressResult(this.policyHolderAddressResult, AddressValidState.Unverified, null, null);
            });

        this.addressForm
            .valueChanges
            .debounceTime(500)
            .subscribe((formValues: AddressViewModel) => {
                const postalCodeControl: AbstractControl = this.addressForm && this.addressForm.controls['postalCode'];

                if (!postalCodeControl || !postalCodeControl.valid || !postalCodeControl.value)
                {
                    this.updateAddressResult(this.policyHolderAddressResult, AddressValidState.Invalid, null, null);

                    return;
                }

                // verify address.
                this.jurisdictionsService
                    .searchResidenceByPostalCode(formValues.postalCode, 'US')
                    .subscribe(result => {
                        if(!result || result.validState !== AddressValidState.Valid) {
                            // show a modal? invalid postal code.
                            this.updateAddressResult(this.policyHolderAddressResult, AddressValidState.Invalid, null, this.jurisdictionsService.invalidPostalCodeErrorMessage);
                            this.modalService.showMessageModal('Invalid Postal Code', this.jurisdictionsService.invalidPostalCodeErrorMessage);

                            return;
                        }

                        if (result.result.stateOrProvince !== this.originalResidenceStateIso2) {
                            // error - show a modal? state mismatch.
                            this.updateAddressResult(this.policyHolderAddressResult, AddressValidState.Invalid, null, this.jurisdictionsService.postalStateMismatchErrorMessage);
                            this.modalService.showMessageModal('Postal Code Mismatch', this.jurisdictionsService.postalStateMismatchErrorMessage);

                            return;
                        }

                        this.updateAddressResult(this.policyHolderAddressResult, result.validState, result.result, null);
                        this.addressForm.patchValue({ city: result.result.city, stateOrProvince: result.result.stateOrProvince }, { emitEvent: false, onlySelf: true });

                        this.addressChanges.next(this.addressForm.value);
                    });
            });

        Observable.merge(this.policyHolderForm.valueChanges, this.additionalTravelerForms.valueChanges, this.newTravelerForms.valueChanges, this.addressChanges.asObservable())
            .subscribe((v) => {
                this.emitValueChanges();
            });

        this.formInitialized = true;
    }

    public canContinue(isQuickQuote: boolean = false): Observable<boolean> {
        return Observable.of(
            (this.policyHolderForm.valid || this.policyHolderForm.disabled) &&
            this.addressForm.valid &&
            (this.additionalTravelerForms.valid || this.additionalTravelerForms.disabled) &&
            this.newTravelerForms.valid &&
            this.policyHolderAddressResult !== null && this.policyHolderAddressResult.validState === AddressValidState.Valid
        );
    }

    public writeValues(data: TravelersData): void {
        // Extract Policy Holder
        Mapper.copyValues(this.policyHolderForm.getRawValue(), data.primaryTraveler);

        // Extract Address
        Mapper.copyValues(this.addressForm.getRawValue(), data.primaryTraveler.address);

        // Extract Additional Travelers
        data.additionalTravelers = [];
        this.additionalTravelerForms.controls.forEach(extractAdditionalTraveler);
        this.newTravelerForms.controls.forEach(extractAdditionalTraveler);

        function extractAdditionalTraveler(control): void {
            if (control instanceof FormGroup) {
                let additionalTraveler: Traveler = control.getRawValue();
                data.additionalTravelers.push(additionalTraveler);
            }
        }
    }

    public canAddTraveler(): boolean {
        // start total at 1, to account for the primary traveler.
        let totalTravelers: number = 1;

        if (this.additionalTravelerForms) {
            totalTravelers += this.additionalTravelerForms.length;
        }

        if (this.newTravelerForms) {
            totalTravelers += this.newTravelerForms.length;
        }

        return this.canAdd && totalTravelers < this.maxTravelers;
    }

    public hasAdditionalTravelers(): boolean {
        return (this.additionalTravelerForms && this.additionalTravelerForms.length > 0) ||
            (this.newTravelerForms && this.newTravelerForms.length > 0);
    }

    public onAddTravelerClick(event: any): void {
        this.newTravelersData.push(new Traveler(false));
        this.newTravelerForms.push(new FormGroup({}));
    }

    public onRemoveTravelerClick(event: any, index: number): void {
        this.travelersData.additionalTravelers.splice(index, 1);
        this.additionalTravelerForms.removeAt(index);
    }

    public onRemoveNewTravelerClick(event: any, index: number): void {
        this.newTravelersData.splice(index, 1);
        this.newTravelerForms.removeAt(index);
    }

    private updateAddressResult(toUpdate: AddressResult, state: AddressValidState, result: IApiAddress, errorMessage: string): void {
        if (!toUpdate) {
            this.modalService.showMessageModal('Development Error', 'Expected an address result to update. This won\'t work without one.');

            return;
        }

        toUpdate.validState = state;
        toUpdate.result = result;
        toUpdate.errorMessage = errorMessage;
    }

    private emitValueChanges(): void {
        let data: TravelersData = new TravelersData();
        this.writeValues(data);
        this.valueChanges.emit(data);
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./src/typescript/policy-edit/travelers.component.ts